// src/pages/Admin/LeadershipAdminPanel.jsx
import { useEffect, useState } from "react";
import {
  getLeadershipPageStatus,
  updateLeadershipPageStatus,
  getLeaders,
  createLeader,
  updateLeader,
  deleteLeader,
} from "../../api/LeadershipApis";
import { useAuth } from "../../context/AuthContext";
import "./css/LeadershipAdminPanel.css";

const ROLE_OPTIONS = [
  { value: "founder", label: "Founder" },
  { value: "co-founder", label: "Co-Founder" },
  { value: "director", label: "Director" },
  { value: "advisor", label: "Advisor" },
  { value: "other", label: "Other" },
];

const EMPTY_FORM = {
  name: "",
  role: "founder",
  title: "",
  qualifications: "",
  quote: "",
  linkedin: "",
  email: "",
  order: 0,
  is_active: true,
};

export default function LeadershipAdminPanel() {
  const { isLoggedIn, user, isLoading: authLoading } = useAuth();
  const [tab, setTab] = useState("visibility");

  // ── Auth guard ──
  if (authLoading) {
    return (
      <div className="h-a-p-page">
        <div className="h-a-p-content-section">
          <h2>🏛️ Leadership</h2>
          <p className="lap-loading">Checking your session…</p>
        </div>
      </div>
    );
  }

  if (!isLoggedIn) {
    return (
      <div className="h-a-p-page">
        <div className="h-a-p-content-section">
          <h2>🏛️ Leadership</h2>
          <div className="lap-error">
            You must be logged in to manage the Leadership page.
            <br />
            Please log in with an admin account and reload this page.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-a-p-page">
      <div className="h-a-p-content-section">
        <h2>🏛️ Leadership</h2>

        {/* Show who's logged in for clarity */}
        <p className="lap-user-banner">
          Signed in as <strong>{user?.username || "unknown"}</strong>
          {user?.is_staff === false && (
            <>
              {" "}
              — <span className="lap-warn">this account is not a staff/admin user</span>
            </>
          )}
        </p>

        <div className="lap-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === "visibility"}
            className={`lap-tab ${tab === "visibility" ? "lap-tab--active" : ""}`}
            onClick={() => setTab("visibility")}
          >
            <i className="fa-solid fa-eye" aria-hidden="true" />
            Page visibility
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "members"}
            className={`lap-tab ${tab === "members" ? "lap-tab--active" : ""}`}
            onClick={() => setTab("members")}
          >
            <i className="fa-solid fa-users" aria-hidden="true" />
            Manage members
          </button>
        </div>

        {tab === "visibility" && <VisibilityTab />}
        {tab === "members" && <MembersTab />}
      </div>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════
   VISIBILITY TAB
   ═════════════════════════════════════════════════════════ */
function VisibilityTab() {
  const [enabled, setEnabled] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    getLeadershipPageStatus()
      .then((res) => {
        if (cancelled) return;
        setEnabled(Boolean(res.data?.is_enabled));
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || "Failed to load settings.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const handleToggle = async () => {
    if (saving || enabled === null) return;

    const next = !enabled;
    setSaving(true);
    setError(null);
    setSaved(false);

    try {
      await updateLeadershipPageStatus(next);
      setEnabled(next);
      setSaved(true);
      sessionStorage.removeItem("leadership-page-enabled");
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      // Better error surfacing
      if (err.response?.status === 403) {
        setError(
          "Permission denied. Your account may not have admin privileges."
        );
      } else if (err.response?.status === 401) {
        setError("Your session expired. Please log in again.");
      } else {
        setError(err.message || "Failed to save.");
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p className="lap-loading">Loading settings…</p>;
  }

  return (
    <>
      <p className="lap-intro">
        Control whether the Leadership page is visible on the public site.
        When disabled, the page redirects to the homepage and the links in
        the navbar and footer disappear.
      </p>

      {error && <div className="lap-error">{error}</div>}

      <div
        className={`lap-status-card ${
          enabled ? "lap-status-card--on" : "lap-status-card--off"
        }`}
      >
        <div className="lap-status-info">
          <div
            className={`lap-status-icon ${
              enabled ? "lap-status-icon--on" : "lap-status-icon--off"
            }`}
            aria-hidden="true"
          >
            <i className={`fa-solid ${enabled ? "fa-eye" : "fa-eye-slash"}`} />
          </div>

          <div className="lap-status-text">
            <div className="lap-status-title">
              {enabled ? "Page is visible" : "Page is hidden"}
            </div>
            <div
              className={`lap-status-desc ${
                enabled ? "lap-status-desc--on" : "lap-status-desc--off"
              }`}
            >
              {enabled
                ? "Visitors can access /leadership and see the links in the navbar and footer."
                : "The Leadership page is hidden from the public site."}
            </div>
          </div>
        </div>

        <div className="lap-status-actions">
          {saved && (
            <span className="lap-saved">
              <i className="fa-solid fa-check" aria-hidden="true" />
              Saved
            </span>
          )}

          <button
            type="button"
            onClick={handleToggle}
            disabled={saving}
            className={`lap-toggle-btn ${
              enabled ? "lap-toggle-btn--hide" : "lap-toggle-btn--show"
            }`}
          >
            <i
              className={`fa-solid ${enabled ? "fa-eye-slash" : "fa-eye"}`}
              aria-hidden="true"
            />
            {saving ? "Saving…" : enabled ? "Hide page" : "Show page"}
          </button>
        </div>
      </div>
    </>
  );
}

/* ═════════════════════════════════════════════════════════
   MEMBERS TAB — unchanged from before
   ═════════════════════════════════════════════════════════ */
function MembersTab() {
  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getLeaders();
      const data = res.data?.results ?? res.data ?? [];
      setLeaders(data);
    } catch (err) {
      setError(err.message || "Failed to load members.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSaved = () => {
    setEditing(null);
    load();
  };

  const handleDelete = async (leader) => {
    if (!window.confirm(`Delete ${leader.name}? This can't be undone.`)) return;
    setDeleting(leader.id);
    try {
      await deleteLeader(leader.id);
      setLeaders((prev) => prev.filter((l) => l.id !== leader.id));
    } catch (err) {
      if (err.response?.status === 403) {
        alert("Permission denied. Admin access required.");
      } else {
        alert(err.message || "Failed to delete.");
      }
    } finally {
      setDeleting(null);
    }
  };

  return (
    <>
      <div className="lap-members-head">
        <p className="lap-intro lap-intro--flush">
          Add, edit, and remove the people shown on the Leadership page.
        </p>
        <button
          type="button"
          className="lap-primary-btn"
          onClick={() => setEditing("new")}
        >
          <i className="fa-solid fa-plus" aria-hidden="true" />
          Add member
        </button>
      </div>

      {error && <div className="lap-error">{error}</div>}

      {loading ? (
        <p className="lap-loading">Loading members…</p>
      ) : leaders.length === 0 ? (
        <div className="lap-empty">
          <i className="fa-solid fa-user-plus" aria-hidden="true" />
          <h3>No members yet</h3>
          <p>Add your first leader to see them on the public page.</p>
        </div>
      ) : (
        <ul className="lap-member-list">
          {leaders.map((leader) => (
            <li key={leader.id} className="lap-member-item">
              <div className="lap-member-avatar">
                {leader.photo ? (
                  <img src={leader.photo} alt={leader.name} />
                ) : (
                  <i className="fa-solid fa-user" aria-hidden="true" />
                )}
              </div>

              <div className="lap-member-info">
                <div className="lap-member-name-row">
                  <strong>{leader.name}</strong>
                  <span className={`lap-role-chip lap-role-chip--${leader.role}`}>
                    {leader.role_display || leader.role}
                  </span>
                  {!leader.is_active && (
                    <span className="lap-chip-inactive">Hidden</span>
                  )}
                </div>
                {leader.title && (
                  <span className="lap-member-title">{leader.title}</span>
                )}
              </div>

              <div className="lap-member-actions">
                <button
                  type="button"
                  className="lap-icon-btn"
                  onClick={() => setEditing(leader)}
                  aria-label={`Edit ${leader.name}`}
                >
                  <i className="fa-solid fa-pen" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="lap-icon-btn lap-icon-btn--danger"
                  onClick={() => handleDelete(leader)}
                  disabled={deleting === leader.id}
                  aria-label={`Delete ${leader.name}`}
                >
                  <i
                    className={`fa-solid ${
                      deleting === leader.id ? "fa-spinner fa-spin" : "fa-trash"
                    }`}
                    aria-hidden="true"
                  />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {editing && (
        <MemberForm
          leader={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={handleSaved}
        />
      )}
    </>
  );
}

/* ═════════════════════════════════════════════════════════
   MEMBER FORM
   ═════════════════════════════════════════════════════════ */
function MemberForm({ leader, onClose, onSaved }) {
  const isEdit = Boolean(leader);

  const [form, setForm] = useState(() =>
    isEdit
      ? {
          name: leader.name || "",
          role: leader.role || "founder",
          title: leader.title || "",
          qualifications: leader.qualifications || "",
          quote: leader.quote || "",
          linkedin: leader.linkedin || "",
          email: leader.email || "",
          order: leader.order ?? 0,
          is_active: leader.is_active ?? true,
        }
      : { ...EMPTY_FORM }
  );

  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(leader?.photo || null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    // Sanity check the token before hitting the API
    const token = localStorage.getItem("access_token");
    if (!token) {
      setError("You're not logged in. Please log in again and retry.");
      setSubmitting(false);
      return;
    }

    const fd = new FormData();
    fd.append("name", form.name);
    fd.append("role", form.role);
    fd.append("title", form.title || "");
    fd.append("qualifications", form.qualifications || "");
    fd.append("quote", form.quote || "");
    fd.append("linkedin", form.linkedin || "");
    fd.append("email", form.email || "");
    fd.append("order", form.order);
    fd.append("is_active", form.is_active);

    if (photoFile) fd.append("photo", photoFile);

    try {
      if (isEdit) {
        await updateLeader(leader.id, fd);
      } else {
        await createLeader(fd);
      }
      onSaved?.();
    } catch (err) {
      const status = err.response?.status;
      const data = err.response?.data;

      if (status === 403) {
        setError(
          "Permission denied. Your account must be a staff/admin user to do this."
        );
      } else if (status === 401) {
        setError("Your session expired. Please log in again.");
      } else if (data && typeof data === "object") {
        setError(
          Object.entries(data)
            .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`)
            .join(" · ")
        );
      } else {
        setError(err.message || "Failed to save.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="lap-modal-backdrop" onClick={onClose}>
      <div
        className="lap-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <header className="lap-modal-head">
          <h3>{isEdit ? "Edit member" : "New member"}</h3>
          <button
            type="button"
            className="lap-icon-btn"
            onClick={onClose}
            aria-label="Close"
          >
            <i className="fa-solid fa-xmark" aria-hidden="true" />
          </button>
        </header>

        <form className="lap-modal-body" onSubmit={handleSubmit}>
          {error && <div className="lap-error">{error}</div>}

          <div className="lap-photo-row">
            <div className="lap-photo-picker">
              {photoPreview ? (
                <img src={photoPreview} alt="Preview" />
              ) : (
                <i className="fa-solid fa-user" aria-hidden="true" />
              )}
            </div>
            <label className="lap-photo-label">
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
              />
              <span>Choose photo</span>
            </label>
          </div>

          <div className="lap-form-grid">
            <label className="lap-form-field">
              <span>Name *</span>
              <input
                type="text"
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                required
              />
            </label>

            <label className="lap-form-field">
              <span>Role *</span>
              <select
                value={form.role}
                onChange={(e) => update("role", e.target.value)}
              >
                {ROLE_OPTIONS.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="lap-form-field">
              <span>Job title</span>
              <input
                type="text"
                value={form.title}
                onChange={(e) => update("title", e.target.value)}
                placeholder="e.g. Chief Executive Officer"
              />
            </label>

            <label className="lap-form-field">
              <span>Display order</span>
              <input
                type="number"
                min="0"
                value={form.order}
                onChange={(e) => update("order", e.target.value)}
              />
            </label>

            <label className="lap-form-field lap-form-field--full">
              <span>Qualifications</span>
              <textarea
                rows="4"
                value={form.qualifications}
                onChange={(e) => update("qualifications", e.target.value)}
                placeholder={"One per line:\nMSc Computer Science, University of Nairobi\nBSc Software Engineering, JKUAT"}
              />
            </label>

            <label className="lap-form-field lap-form-field--full">
              <span>Quote</span>
              <textarea
                rows="3"
                value={form.quote}
                onChange={(e) => update("quote", e.target.value)}
                placeholder="A short personal statement or vision quote."
              />
            </label>

            <label className="lap-form-field">
              <span>LinkedIn URL</span>
              <input
                type="url"
                value={form.linkedin}
                onChange={(e) => update("linkedin", e.target.value)}
                placeholder="https://linkedin.com/in/..."
              />
            </label>

            <label className="lap-form-field">
              <span>Email</span>
              <input
                type="email"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                placeholder="name@hayducate.com"
              />
            </label>

            <label className="lap-form-check lap-form-field--full">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => update("is_active", e.target.checked)}
              />
              <span>Visible on the public Leadership page</span>
            </label>
          </div>

          <footer className="lap-modal-foot">
            <button
              type="button"
              className="lap-ghost-btn"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="lap-primary-btn"
              disabled={submitting}
            >
              {submitting
                ? "Saving…"
                : isEdit
                ? "Save changes"
                : "Create member"}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
}